import classnames from 'classnames';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { ReactComponent as IconArrowNext } from '../../icons/icon-arrow-next.svg';
import { ReactComponent as IconArrowPrev } from '../../icons/icon-arrow-previous.svg';
import { scrollToAnchor } from '../../utils/dom';
import { replaceQueryParam2 } from '../../utils/routing';
import { isBrowser, isMobile } from '../../utils/web';
import { Link } from '../Link';
import { PaginationMetaLink } from './PaginationMetaLink';

const computeVisiblePages = ({ totalPages, page }: { totalPages: number; page: number }) => {
  const pagesOf = isMobile() ? 1 : 2;
  const count = Math.min(pagesOf * 2 + 1, totalPages);

  if (!count) return [];

  const start = Math.max(Math.min(page - pagesOf, totalPages - count), 1);
  return Array(count)
    .fill(undefined)
    .map((_, index) => index + start);
};

export type PaginationPropTypes = {
  count: number;
  limit: number;
  page: number;
  cmp?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  queryName: string;
  anchor?: string;
  loading: boolean;
  className?: string;
  children?: ReactNode;
};

const PaginationLink: React.FC<PaginationPropTypes> = memo(
  ({ children, page, queryName, className, anchor }) => {
    const router = useRouter();

    const params = useMemo(
      () => ({
        ...router.query,
        [queryName]: page >= 2 ? page : undefined,
      }),
      [page, queryName, router.query],
    );

    const onClick = useCallback(() => {
      if (anchor) {
        scrollToAnchor({
          anchorName: anchor,
          options: { behavior: 'smooth' },
        });
      }
    }, [anchor]);

    return (
      <Link to={router.pathname} params={params} shallow scroll={false}>
        <a className={className} onClick={onClick}>
          {children || page}
        </a>
      </Link>
    );
  },
);

const computeElementProps = ({ currentPage, page }: { currentPage: number; page: number }) => ({
  page,
  className: classnames(
    'flex items-center outline-none h-8 p-1 px-3 rounded text-center',
    currentPage === page
      ? 'cursor-default bg-primary-base text-white'
      : 'cursor-pointer hover:bg-gray-lighter',
  ),
});

const diff = isMobile() ? 100000 : 10;

/**
 *
 * @param count
 * @param limit
 * @param page
 * @param RawCmp
 * @param queryName
 * @param anchor
 * @param loading
 * @constructor
 */
export const Pagination = ({
  count,
  limit,
  page,
  queryName,
  anchor,
  loading,
}: PaginationPropTypes) => {
  const router = useRouter();
  const totalPages = useMemo(() => Math.ceil(count / limit), [count, limit]) || 0;

  const visiblePages = useMemo(() => computeVisiblePages({ totalPages, page }), [totalPages, page]);
  const firstVisiblePage = visiblePages[0];
  const lastVisiblePage = visiblePages[visiblePages.length - 1];
  const beforePage = firstVisiblePage - diff;
  const afterPage = lastVisiblePage + diff;

  const Cmp = useCallback(
    props => <PaginationLink {...props} queryName={queryName} anchor={anchor} />,
    [queryName, anchor],
  );

  const computeProps = useCallback(
    targetPage => computeElementProps({ currentPage: page, page: targetPage }),
    [page],
  );

  useEffect(() => {
    if (!loading) {
      const wrongPage = queryName === 'page' && page > 1 && (page - 1) * limit >= count;
      if (!wrongPage || !isBrowser) return;
      const lastPage = Math.ceil(count / limit);
      const { href, as } = replaceQueryParam2(
        router.pathname,
        router.query,
        router.asPath,
        queryName,
        lastPage < 2 ? null : lastPage,
      );
      router.replace(href, as, { shallow: true, scroll: false });
    }
  }, [router, queryName, page, count, limit, loading]);

  if (totalPages < 2) return null;

  return (
    <ul className="flex list-none space-x-1 justify-center items-center mt-2 pb-2 cursor-default whitespace-nowrap">
      <PaginationMetaLink page={page} queryName={queryName} totalPages={totalPages} />

      {page > 1 && page <= totalPages + 1 && (
        <li>
          <Cmp {...computeProps(page - 1)}>
            <IconArrowPrev />
          </Cmp>
        </li>
      )}

      {firstVisiblePage > 1 && (
        <li>
          <Cmp {...computeProps(1)} />
        </li>
      )}

      {firstVisiblePage > 2 && <li>...</li>}

      {beforePage > 2 && (
        <>
          <li>
            <Cmp {...computeProps(beforePage)} />
          </li>
          <li>...</li>
        </>
      )}

      {visiblePages.map(vPage => (
        <li key={vPage}>
          <Cmp {...computeProps(vPage)} />
        </li>
      ))}

      {afterPage < totalPages - 2 && (
        <>
          <li>...</li>
          <li>
            <Cmp {...computeProps(afterPage)} />
          </li>
        </>
      )}

      {lastVisiblePage < totalPages - 1 && <li>...</li>}

      {lastVisiblePage !== totalPages && (
        <li>
          <Cmp {...computeProps(totalPages)} />
        </li>
      )}

      {page > -1 && page < totalPages && (
        <li>
          <Cmp {...computeProps(page + 1)}>
            <IconArrowNext />
          </Cmp>
        </li>
      )}
    </ul>
  );
};
